import { inject, untracked } from '@angular/core';
import { SavedCartSearchItem, SavedCartSearchResult, ShoppingCartApi } from '@idealsupply/ngclient-webservice-shopping-cart';
import { signalStore, withMethods } from '@ngrx/signals';
import { DialogService } from 'common-ui';
import { firstValueFrom } from 'rxjs';
import { withCachedEntityStore } from 'state-data';
import { CartsStore } from './carts.store';
import { SavedCartDetailsStore } from './saved-cart-detail.store';

export const SavedCartStore = signalStore(
  { providedIn: 'root' },
  withCachedEntityStore({
    selectId: (entity: SavedCartSearchResult) => 'all',
    load: (id: string) => {
      const searchAPI = inject(ShoppingCartApi);
      return searchAPI.getSavedCarts();
    },
  }),
  withMethods((store) => {
    const api = inject(ShoppingCartApi);
    const cartsStore = inject(CartsStore);
    const savedCartDetailsStore = inject(SavedCartDetailsStore);
    const dialogService = inject(DialogService);
    const getAll = () => store.get('all');

    const getItems = () => {
      const data = untracked(getAll());
      let items = [] as SavedCartSearchItem[];
      if (data.entity && !data.error && !data.loading) {
        items = data.entity.data;
      }
      return items;
    };

    const saveCartAs = async (name: string) => {
      const items = getItems();
      const newItem = {
        id: -1,
        name,
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
      };
      items.push(newItem);
      store.setData({
        data: items,
        limit: 0,
        skip: 0,
        total: items.length,
      });
      const mainCart = untracked(cartsStore.mainCart);
      if (mainCart) {
        const result = await firstValueFrom(api.createSavedCart(mainCart.id, name));
        store.setData({
          data: items.map((item) => {
            if (item === newItem) {
              item = {
                ...item,
                id: result.id,
              };
            }
            return item;
          }),
          limit: 0,
          skip: 0,
          total: items.length,
        });
        dialogService.notice(`Cart saved successfully.`, { duration: 5000 });
      }
    };

    const replaceSavedCart = async (id: number) => {
      const mainCart = untracked(cartsStore.mainCart);
      if (mainCart) {
        const result = await firstValueFrom(api.addToSavedCart(id, 'Replace', mainCart.id));

        const existingCarts = getItems();
        const existingCartIdx = existingCarts.findIndex((item) => item.id === id);
        if (existingCartIdx !== -1) {
          const existingCart = existingCarts[existingCartIdx];
          const newCart: SavedCartSearchItem = {
            ...existingCart,
            name: result.name,
            created: result.created,
            updated: result.updated,
          };
          existingCarts.splice(existingCartIdx, 1, newCart);
        }

        store.setData({
          data: [...existingCarts],
          limit: 0,
          skip: 0,
          total: existingCarts.length,
        });

        savedCartDetailsStore.setData(result);

        dialogService.notice(`Cart updated successfully.`, { duration: 5000 });
      }
    };

    const mergeToSavedCart = async (id: number) => {
      const mainCart = untracked(cartsStore.mainCart);
      if (mainCart) {
        const result = await firstValueFrom(api.addToSavedCart(id, 'Merge', mainCart.id));

        const existingCarts = getItems();
        const existingCartIdx = existingCarts.findIndex((item) => item.id === id);
        if (existingCartIdx !== -1) {
          const existingCart = existingCarts[existingCartIdx];
          const newCart: SavedCartSearchItem = {
            ...existingCart,
            name: result.name,
            created: result.created,
            updated: result.updated,
          };
          existingCarts.splice(existingCartIdx, 1, newCart);
        }

        store.setData({
          data: [...existingCarts],
          limit: 0,
          skip: 0,
          total: existingCarts.length,
        });
        savedCartDetailsStore.setData(result);
        dialogService.notice(`Cart updated successfully.`, { duration: 5000 });
      }
    };

    const deleteSavedCart = async (id: number) => {
      await firstValueFrom(api.deleteSavedCart(id));
      const items = getItems().filter((item) => item.id !== id);
      store.setData({
        data: items,
        limit: 0,
        skip: 0,
        total: items.length,
      });

      dialogService.notice(`Cart deleted successfully.`, { duration: 5000 });
    };

    return {
      saveCartAs,
      replaceSavedCart,
      mergeToSavedCart,
      deleteSavedCart,
      getAll,
    };
  }),
);
